import { create } from 'zustand'
import { describePhase, type GenerationPhaseEvent, type PhaseStep } from '../process/phase-steps'

export type { GenerationPhaseEvent, PhaseStep } from '../process/phase-steps'

interface CreativePhaseState {
  steps: PhaseStep[]
  active: boolean
}

interface GenerationPhaseStore {
  byCreative: Record<string, CreativePhaseState>
  applyPhase: (event: GenerationPhaseEvent) => void
  clear: (creativeId: string) => void
}

/** Pending post-finalize auto-clear timers, keyed by creativeId, so a new run can cancel a stale one. */
const clearTimers = new Map<string, ReturnType<typeof setTimeout>>()

export const useGenerationPhaseStore = create<GenerationPhaseStore>((set, get) => ({
  byCreative: {},

  applyPhase: event => {
    const creativeId = event.creativeId
    if (!creativeId) return
    // Any new beat means a run is live again — cancel a pending auto-clear so the
    // previous run's timer can't wipe this run's ladder mid-generation.
    const pending = clearTimers.get(creativeId)
    if (pending) {
      clearTimeout(pending)
      clearTimers.delete(creativeId)
    }
    set(state => {
      const prev = state.byCreative[creativeId]
      // Start a fresh ladder when a new run begins: the explicit 'decide' start beat,
      // the first beat we've ever seen, OR any beat that arrives after the previous run
      // already finalized (active=false). The last case is essential for runs whose first
      // beat ISN'T 'decide' (a reel/carousel revise skips decide) — without it, a second
      // revise within the auto-clear window would append onto the finished run's steps.
      const isFreshRun =
        (event.stage === 'decide' && event.status === 'start') || !prev || !prev.active
      const base: CreativePhaseState = isFreshRun ? { steps: [], active: true } : prev

      const { id, label, detail } = describePhase(event)
      const steps = [...base.steps]
      const idx = steps.findIndex(s => s.id === id)
      const status: PhaseStep['status'] = event.status === 'done' ? 'done' : 'active'
      // Any newly active step means earlier active steps are effectively done.
      const promoted = steps.map(s =>
        s.status === 'active' ? { ...s, status: 'done' as const } : s
      )
      const next: PhaseStep = { id, label, detail, status }
      if (idx >= 0) promoted[idx] = { ...promoted[idx], ...next }
      else promoted.push(next)

      // The finalize 'done' beat ends the run; the message's tool result + score
      // chip take over from here.
      const active = !(event.stage === 'finalize' && event.status === 'done')
      return { byCreative: { ...state.byCreative, [creativeId]: { steps: promoted, active } } }
    })
    // Auto-clear shortly after a run ends so a stale ladder never lingers — but only
    // if no newer run has started in the meantime (the timer is cancelled on any new
    // beat above, and re-checks active before clearing).
    if (event.stage === 'finalize' && event.status === 'done') {
      const timer = setTimeout(() => {
        clearTimers.delete(creativeId)
        if (!get().byCreative[creativeId]?.active) get().clear(creativeId)
      }, 1500)
      clearTimers.set(creativeId, timer)
    }
  },

  clear: creativeId =>
    set(state => {
      const { [creativeId]: _omit, ...rest } = state.byCreative
      return { byCreative: rest }
    }),
}))
